import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denyModal, setDenyModal] = useState(null);
  const [denyReason, setDenyReason] = useState('');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await API.get('/admin/users');
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await API.put(`/admin/users/${userId}/approve`);
      setUsers(prev => prev.map(u =>
        u.user_id === userId ? { ...u, is_approved: true, is_denied: false } : u
      ));
      toast.success('User approved');
    } catch {
      toast.error('Failed to approve user');
    }
  };

  const denyUser = async () => {
    try {
      await API.put(`/admin/users/${denyModal.user_id}/deny`, { reason: denyReason });
      setUsers(prev => prev.map(u =>
        u.user_id === denyModal.user_id
          ? { ...u, is_denied: true, is_approved: false, denial_reason: denyReason }
          : u
      ));
      toast.success('User denied');
      setDenyModal(null);
      setDenyReason('');
    } catch {
      toast.error('Failed to deny user');
    }
  };

  const changeRole = async (userId, role) => {
    try {
      await API.put(`/admin/users/${userId}/role`, { role });
      setUsers(prev => prev.map(u =>
        u.user_id === userId ? { ...u, role } : u
      ));
      toast.success('Role updated');
    } catch {
      toast.error('Failed to update role');
    }
  };

  const deleteUser = async (userId) => {
  if (!window.confirm('Permanently delete this user record?')) return;
  try {
    await API.delete(`/admin/users/${userId}`);
    setUsers(prev => prev.filter(u => u.user_id !== userId));
    toast.success('User record deleted');
  } catch {
    toast.error('Failed to delete user');
  }
};

const resetTwoFactor = async (userId, email) => {
    if (!window.confirm(`Reset 2FA for ${email}? They'll log in with just their password and set it up again.`)) return;
    try {
      await API.put(`/admin/users/${userId}/reset-2fa`);
      setUsers(prev => prev.map(u =>
        u.user_id === userId ? { ...u, totp_enabled: false } : u
      ));
      toast.success(`2FA reset for ${email}`);
    } catch {
      toast.error('Failed to reset 2FA');
    }
  };

  const pending = users.filter(u => !u.is_approved && !u.is_denied);
  const denied  = users.filter(u => u.is_denied);
  const active  = users.filter(u => u.is_approved);

  const roleBadge = (role) => ({
    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
    background: role === 'admin' ? '#E1E9F2' : role === 'boss' ? '#FDF4DD' : '#E9F7EF',
    color: role === 'admin' ? '#1A2A3A' : role === 'boss' ? '#F5A800' : '#15803D'
  });

  const thStyle = {
    padding: '10px 12px', background: '#1A2A3A', color: 'white',
    fontSize: '13px', fontWeight: '500', textAlign: 'left',
    borderRight: '1px solid #F5A800'
  };

  const tdStyle = { padding: '12px', borderBottom: '1px solid #E2E8F0' };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '950px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Users</h1>
          <p style={{ color: '#5B6B7F', fontSize: '14px' }}>Approve or deny account requests and manage roles</p>
        </div>
        <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
          ← Dashboard
        </button>
      </div>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div style={{ marginBottom: '28px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: '#dc2626' }}>
            ⚠ Pending Approval ({pending.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pending.map(u => (
              <div key={u.user_id} style={{ background: '#FCEBEB', border: '1px solid #F5B5B5', borderRadius: '8px', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontWeight: '600' }}>{u.email}</p>
                  <p style={{ fontSize: '12px', color: '#5B6B7F', marginTop: '2px' }}>
                    Requested {new Date(u.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => approveUser(u.user_id)}
                    className="fcc-press"
                    style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => { setDenyModal(u); setDenyReason(''); }}
                    className="fcc-press"
                    style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                  >
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

     {/* Denied accounts */}
        {denied.length > 0 && (
        <div style={{ marginBottom: '28px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: '#5B6B7F' }}>
            Denied ({denied.length})
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {denied.map(u => (
                <div key={u.user_id} style={{ background: '#F4F7FB', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.8 }}>
                <div>
                    <p style={{ fontWeight: '600', color: '#5B6B7F' }}>{u.email}</p>
                    {u.denial_reason && (
                    <p style={{ fontSize: '12px', color: '#8595A6', marginTop: '2px' }}>
                        Reason: {u.denial_reason}
                    </p>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                    onClick={() => approveUser(u.user_id)}
                    className="fcc-press"
                    style={{ background: 'none', border: '1px solid #15803D', color: '#15803D', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' }}
                    >
                    Approve anyway
                    </button>
                    <button
                    onClick={() => deleteUser(u.user_id)}
                    className="fcc-press"
                    style={{ background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' }}
                    >
                    Delete
                    </button>
                </div>
                </div>
            ))}
            </div>
        </div>
        )}

      {/* Active users */}
      <div>
        <h2 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>
          Active Users ({active.length})
        </h2>
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr>
                {['Email', 'Role', 'Last Login', '2FA', 'Change Role'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {active.map(u => (
                <tr key={u.user_id}>
                  <td style={tdStyle}>{u.email}</td>
                  <td style={tdStyle}><span style={roleBadge(u.role)}>{u.role}</span></td>
                  <td style={{ ...tdStyle, color: '#5B6B7F', fontSize: '13px' }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {u.totp_enabled ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <span>✓</span>
                        <button
                          onClick={() => resetTwoFactor(u.user_id, u.email)}
                          className="fcc-press"
                          style={{ background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px' }}
                        >
                          Reset
                        </button>
                      </div>
                    ) : '—'}
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={u.role}
                      onChange={e => changeRole(u.user_id, e.target.value)}
                      style={{ padding: '5px 8px', border: '1px solid #E2E8F0', borderRadius: '4px', fontSize: '13px', outline: 'none' }}
                    >
                      <option value="employee">Employee</option>
                      <option value="boss">Boss</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deny modal */}
      {denyModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="fcc-modal-pop" style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Deny Account Request</h2>
            <p style={{ color: '#5B6B7F', fontSize: '14px', marginBottom: '20px' }}>{denyModal.email}</p>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Reason (optional)
              </label>
              <textarea
                rows={3}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none', resize: 'vertical' }}
                placeholder="e.g. Not a recognized employee..."
                value={denyReason}
                onChange={e => setDenyReason(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDenyModal(null); setDenyReason(''); }}
                className="fcc-press"
                style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 18px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}
              >
                Cancel
              </button>
              <button
                onClick={denyUser}
                className="fcc-press"
                style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '9px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
              >
                Deny Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;